import CommonModal from "components/admin/CommonModal";
import ViewTicketDetailsForm from "components/admin/ViewTicketDetailsForm";
import { useEffect, useState } from "react";
import { IoSearchOutline } from "react-icons/io5";
import TableSkeleton from "components/admin/TableSkeleton";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { MdEdit } from "react-icons/md";
import { Link, useParams } from "react-router-dom";
import apis from "services";
import { toast } from "react-toastify";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { formatDate } from "utils/formatDate";

const ManageAllTickets = () => {
  const limit = 10;
  const { lang } = useParams();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });
  const [debounceTimeout, setDebounceTimeout] = useState(null);

  const fetchAllTickets = async () => {
    setLoading(true);
    try {
      const res = await apis.getAllTickets({ search, page, limit }); // Pass search as a query param
      if (res?.data?.status) {
        setTickets(res?.data?.data?.tickets);
        setPagination({
          total: res.data.data.total,
          page: res.data.data.page,
          limit: res.data.data.limit,
          totalPages: res.data.data.totalPages,
        });
      } else {
        toast.error(
          res?.data?.message || "Something went wrong, Please try again"
        );
      }
    } catch (e) {
      toast.error(e.message || "Something went wrong, Please try again");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch tickets whenever the search changes
    fetchAllTickets();
  }, [search, page]);

  const handleDeleteTicket = async (ticketId) => {
    try {
      setLoading(true);
      const res = await apis.deleteTicketById(ticketId);
      if (res?.data?.status) {
        toast.success(res.data.message);
        fetchAllTickets(search); // Fetch tickets with the current search term
      } else {
        toast.error(res.data.message || "Something went wrong, Please wait!");
      }
    } catch (e) {
      toast.error(e.message || "Something went wrong, Please wait!");
    } finally {
      setLoading(false);
    }
  };

  const onSearch = (value) => {
    const trimmedValue = value.trim();
    // Clear the previous timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    // Set a new timeout to delay the API call
    const timeout = setTimeout(() => {
      setSearch(trimmedValue); // Update search after debounce
      setPage(1);
    }, 1000); // Delay of 1 second

    setDebounceTimeout(timeout);
  };

  const onPageChange = (type) => {
    if (
      (page == 1 && type == "prev") ||
      (page >= pagination.totalPages && type == "next")
    )
      return;
    type === "next" ? setPage(page + 1) : setPage(page - 1);
  };

  return (
    <div className="tickets-tab-container">
      <div className="ticket-input">
        <label htmlFor={"searchedTicketId"}>
          Search ticket by user name and subject
        </label>
        <div className="input">
          <div>
            <div>
              <IoSearchOutline />
            </div>
          </div>

          <input
            type="text"
            name="searchedTicketId"
            id="searchedTicketId"
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="ticket-table-wrapper">
        <table className="ticket-history-table">
          <thead>
            <tr className="tickets-table-head">
              <th>Ticket Id</th>
              <th>User Name</th>
              <th>Subject</th>
              <th>Attachment</th>
              <th>Priority</th>
              <th>Date</th>
              <th>Category</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <TableSkeleton rows={5} cols={9} />
            ) : !tickets?.length ? (
              <tr>
                <p className="no-record-found">No Record Found</p>
              </tr>
            ) : (
              tickets?.map((ticket, index) => (
                <tr key={index}>
                  <td>{ticket._id}</td>
                  <td>{ticket.userId?.name}</td>
                  <td>{ticket.subject}</td>
                  <td>
                    <img
                      src={`${process.env.REACT_APP_SERVER_URL}${ticket.attachment}`}
                      alt={ticket.subject}
                      className="ticket-image"
                    />
                  </td>
                  <td>
                    <span
                      className={`custom-badge ${
                        ticket.priority === "high"
                          ? "red-badge"
                          : ticket.priority === "medium"
                          ? "orange-badge"
                          : "green-badge"
                      }`}
                    >
                      {ticket.priority}
                    </span>
                  </td>
                  <td>{formatDate(ticket.createdAt)}</td>
                  <td>{ticket.category}</td>
                  <td>
                    <span
                      className={`custom-badge ${
                        ticket.status === "open"
                          ? "orange-badge"
                          : ticket.status === "closed"
                          ? "green-badge"
                          : "yellow-badge"
                      }`}
                    >
                      {ticket.status}
                    </span>
                  </td>
                  <td className="actions-column">
                    <div>
                      <CommonModal>
                        <ViewTicketDetailsForm ticketId={ticket._id} />
                      </CommonModal>
                      <Link to={`/${lang}/admin/tickets/${ticket._id}`}>
                        <MdEdit className="action-btn" />
                      </Link>
                      <RiDeleteBin6Fill
                        className="text-red action-btn"
                        onClick={() => handleDeleteTicket(ticket._id)}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!loading && (
        <div className="pagination-container">
          <div>Total: {pagination.total}</div>
          <div>
            <button
              className={`paginator`}
              disabled={page == 1}
              onClick={() => onPageChange("prev")}
            >
              <IoIosArrowBack />
            </button>
            Page {pagination.page} of {pagination.totalPages}
            <button
              className={`paginator`}
              disabled={page >= pagination.totalPages}
              onClick={() => onPageChange("next")}
            >
              <IoIosArrowForward />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAllTickets;
